import { TYRES } from "@/lib/format";

const PSI = 14.5038;
const norm = (s) => (s || "").trim().toLowerCase();

export const toBar = (v, unit) => (v == null || v === "" || Number.isNaN(Number(v)) ? null : unit === "psi" ? Number(v) / PSI : Number(v));
export const fromBar = (v, unit) => (unit === "psi" ? v * PSI : v);
export const fmtP = (v, unit = "bar", sign = false) =>
  v == null ? "—" : `${sign && v > 0 ? "+" : ""}${fromBar(v, unit).toFixed(unit === "psi" ? 1 : 2)}`;

export const tyreLabel = (brand, model) => [brand, model].filter((x) => (x || "").trim()).join(" · ");

export function findTarget(targets, brand, model) {
  if (!norm(brand) && !norm(model)) return null;
  return (targets || []).find((x) => norm(x.brand) === norm(brand) && norm(x.model) === norm(model)) || null;
}

export function stintsOf(s) {
  const base = { session: s, track_temp: s.conditions?.track_temp ?? null, air_temp: s.conditions?.air_temp ?? null, humidity: s.conditions?.humidity ?? null, wind_speed: s.conditions?.wind_speed ?? null, date: s.session_date || "", unit: s.pressure_unit || "bar" };
  const out = [{ ...base, n: 1, key: s.id, tyres: s.tyres, brand: s.tyre_brand, model: s.tyre_model }];
  if (s.pit) out.push({ ...base, n: 2, key: `${s.id}-s2`, tyres: s.tyres2, brand: s.tyre_brand2 || s.tyre_brand, model: s.tyre_model2 || s.tyre_model });
  return out;
}

const avg = (xs) => (xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : null);

export function analyzeStint(st, target) {
  const tol = target?.tolerance ?? 0.05;
  const wheels = Object.fromEntries(TYRES.map((p) => {
    const cold = toBar(st.tyres?.[p]?.cold, st.unit);
    const hot = toBar(st.tyres?.[p]?.hot, st.unit);
    const gain = cold != null && hot != null ? hot - cold : null;
    const delta = target && hot != null ? hot - target.target : null;
    const status = delta == null ? "na" : Math.abs(delta) <= tol + 1e-9 ? "ok" : delta > 0 ? "high" : "low";
    return [p, { cold, hot, gain, delta, status }];
  }));
  const ds = TYRES.map((p) => wheels[p].delta).filter((d) => d != null);
  const statuses = TYRES.map((p) => wheels[p].status).filter((s) => s !== "na");
  const overall = !statuses.length ? "na" : statuses.every((s) => s === "ok") ? "ok"
    : statuses.every((s) => s !== "low") ? "high" : statuses.every((s) => s !== "high") ? "low" : "mixed";
  const side = (ps) => avg(ps.map((p) => wheels[p].hot).filter((v) => v != null));
  return {
    target, tol, wheels, overall, avgDelta: avg(ds),
    worst: ds.length ? ds.reduce((a, b) => (Math.abs(b) > Math.abs(a) ? b : a)) : null,
    axle: side(["fl", "fr"]) != null && side(["rl", "rr"]) != null ? side(["fl", "fr"]) - side(["rl", "rr"]) : null,
    lr: side(["fl", "rl"]) != null && side(["fr", "rr"]) != null ? side(["fl", "rl"]) - side(["fr", "rr"]) : null,
  };
}

function fit(points) {
  const n = points.length;
  const mx = avg(points.map((p) => p.x)), my = avg(points.map((p) => p.y));
  const sxx = points.reduce((a, p) => a + (p.x - mx) ** 2, 0);
  if (!n) return null;
  const range = Math.max(...points.map((p) => p.x)) - Math.min(...points.map((p) => p.x));
  if (n < 3 || range < 4 || sxx === 0) return { n, mean: my, slope: null };
  const slope = points.reduce((a, p) => a + (p.x - mx) * (p.y - my), 0) / sxx;
  return { n, mean: my, slope, a: my - slope * mx };
}

export function gainModels(stints) {
  return Object.fromEntries(TYRES.map((p) => {
    const pts = stints.map((st) => {
      const c = toBar(st.tyres?.[p]?.cold, st.unit), h = toBar(st.tyres?.[p]?.hot, st.unit);
      return c != null && h != null && st.track_temp != null ? { x: Number(st.track_temp), y: h - c } : null;
    }).filter(Boolean);
    return [p, fit(pts)];
  }));
}

export const predictGain = (m, temp) => (!m ? null : m.slope != null && temp != null && temp !== "" ? m.a + m.slope * Number(temp) : m.mean);

export function impactOf(stints, key) {
  const pts = stints.map((st) => {
    const gs = TYRES.map((p) => { const c = toBar(st.tyres?.[p]?.cold, st.unit), h = toBar(st.tyres?.[p]?.hot, st.unit); return c != null && h != null ? h - c : null; }).filter((g) => g != null);
    const x = st[key];
    return gs.length && x != null && x !== "" ? { x: Number(x), y: avg(gs) } : null;
  }).filter(Boolean);
  const n = pts.length;
  if (n < 3) return { n };
  const mx = avg(pts.map((p) => p.x)), my = avg(pts.map((p) => p.y));
  const sxx = pts.reduce((a, p) => a + (p.x - mx) ** 2, 0), syy = pts.reduce((a, p) => a + (p.y - my) ** 2, 0);
  const sxy = pts.reduce((a, p) => a + (p.x - mx) * (p.y - my), 0);
  if (sxx === 0) return { n, flat: true };
  return { n, slope: sxy / sxx, r: syy === 0 ? 0 : sxy / Math.sqrt(sxx * syy), min: Math.min(...pts.map((p) => p.x)), max: Math.max(...pts.map((p) => p.x)) };
}

const num = (v) => (v === "" || v == null || Number.isNaN(Number(v)) ? null : Number(v));

export function condModel(stints, p) {
  const pts = stints.map((st) => {
    const c = toBar(st.tyres?.[p]?.cold, st.unit), h = toBar(st.tyres?.[p]?.hot, st.unit);
    return c != null && h != null ? { t: num(st.track_temp), a: num(st.air_temp), g: h - c } : null;
  }).filter(Boolean);
  if (!pts.length) return null;
  const mean = avg(pts.map((x) => x.g));
  const both = pts.filter((x) => x.t != null && x.a != null);
  const span = (xs) => (xs.length ? Math.max(...xs) - Math.min(...xs) : 0);
  if (both.length >= 4 && span(both.map((x) => x.t)) >= 3 && span(both.map((x) => x.a)) >= 2) {
    const mt = avg(both.map((x) => x.t)), ma = avg(both.map((x) => x.a)), mg = avg(both.map((x) => x.g));
    let stt = 0, saa = 0, sta = 0, stg = 0, sag = 0;
    both.forEach((x) => { const dt = x.t - mt, da = x.a - ma, dg = x.g - mg; stt += dt * dt; saa += da * da; sta += dt * da; stg += dt * dg; sag += da * dg; });
    const det = stt * saa - sta * sta;
    if (det > 1e-6 && det / (stt * saa) > 0.1) {
      const b = (stg * saa - sag * sta) / det, c = (sag * stt - stg * sta) / det;
      return { n: pts.length, kind: "both", bt: b, ba: c, predict: (T, A) => mg + b * ((num(T) ?? mt) - mt) + c * ((num(A) ?? ma) - ma) };
    }
  }
  for (const [k, kind] of [["t", "track"], ["a", "air"]]) {
    const xs = pts.filter((x) => x[k] != null);
    const f = fit(xs.map((x) => ({ x: x[k], y: x.g })));
    if (f?.slope != null && xs.length >= 3) {
      return { n: pts.length, kind, [kind === "track" ? "bt" : "ba"]: f.slope, predict: (T, A) => { const v = num(kind === "track" ? T : A); return v == null ? f.mean : f.a + f.slope * v; } };
    }
  }
  return { n: pts.length, kind: "mean", predict: () => mean };
}

export function nextColdFromSession(a, models, curTemp, nextTemp) {
  if (!a.target) return {};
  return Object.fromEntries(TYRES.map((p) => {
    const w = a.wheels[p];
    if (w.gain == null) return [p, null];
    const slope = models?.[p]?.slope;
    const dT = slope != null && curTemp != null && nextTemp !== "" && nextTemp != null ? Number(nextTemp) - Number(curTemp) : 0;
    return [p, a.target.target - (w.gain + (slope != null ? slope * dT : 0))];
  }));
}

export function buildTips(a, t, unit, next, nextTemp, curTemp) {
  const tips = [];
  if (!a.target) return [{ kind: "warn", text: t("no_target") }];
  if (a.overall === "na") return [{ kind: "warn", text: t("tip_missing_hot") }];
  if (a.overall === "ok") tips.push({ kind: "ok", text: t("tip_all_ok") });
  TYRES.forEach((p) => {
    const w = a.wheels[p];
    if (w.status === "high" || w.status === "low") {
      tips.push({ kind: w.status, text: t("tip_wheel").replace("{w}", t(`tyre_${p}`)).replace("{d}", fmtP(w.delta, unit, true)).replace("{c}", fmtP(next[p], unit)).replaceAll("{u}", unit) });
    }
  });
  if (a.axle != null && Math.abs(a.axle) > a.tol) tips.push({ kind: "info", text: t(a.axle > 0 ? "tip_axle_front" : "tip_axle_rear").replace("{d}", fmtP(Math.abs(a.axle), unit)).replaceAll("{u}", unit) });
  if (a.lr != null && Math.abs(a.lr) > a.tol) tips.push({ kind: "info", text: t(a.lr > 0 ? "tip_side_left" : "tip_side_right").replace("{d}", fmtP(Math.abs(a.lr), unit)).replaceAll("{u}", unit) });
  if (nextTemp !== "" && nextTemp != null && curTemp != null && Number(nextTemp) !== Number(curTemp)) {
    tips.push({ kind: "info", text: t("tip_temp").replace("{t}", nextTemp).replace("{d}", `${Number(nextTemp) > Number(curTemp) ? "+" : ""}${Number(nextTemp) - Number(curTemp)}`) });
  }
  return tips;
}

export const STATUS_CLS = {
  ok: "text-[#10B981] border-[#10B981]/50 bg-[#10B981]/10",
  high: "text-[#FF3B30] border-[#FF3B30]/50 bg-[#FF3B30]/10",
  low: "text-[#007AFF] border-[#007AFF]/50 bg-[#007AFF]/10",
  mixed: "text-[#FFB800] border-[#FFB800]/50 bg-[#FFB800]/10",
  na: "text-slate-500 border-[#282D3D]",
};
